import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "text-center" }
const _hoisted_2 = {
  key: 0,
  id: "video-container-id",
  class: "video-container bg-primary q-pa-sm"
}
const _hoisted_3 = ["width"]
const _hoisted_4 = /*#__PURE__*/_createElementVNode("p", { class: "vjs-no-js" }, [
  /*#__PURE__*/_createTextVNode(" Pour voir cette video, merci d'activer Javascript et pensez à utiliser un navigateur qui "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://videojs.com/html5-video-support/",
    target: "_blank"
  }, " supporte 'HTML5 video' ")
], -1 /* HOISTED */)
const _hoisted_5 = [
  _hoisted_4
]
const _hoisted_6 = {
  id: "video_legend",
  class: "video-legend q-mt-sm text-white"
}
const _hoisted_7 = { lang: "ja" }

import type { PropType } from 'vue';
import {
  ref,
  toRefs,
  computed,
  onMounted,
  onBeforeUnmount,
  watch
} from 'vue';

// Videojs
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import Tech from 'video.js/dist/types/tech/tech';
import 'video.js/dist/video-js.css';
import frJson from '@shared/conf/videojs-fr.json';

// @silvermine/videojs-chromecast
import chromecast from '@silvermine/videojs-chromecast';
import '@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Stores
import { useUiStore } from 'src/stores/ui';

// Custom
import endpoints from '@shared/service/endpoint/endpoints.class';
import {
  BroadcastChannelEnum,
  BroadcastChannelMessageEnum,
} from '@shared/enum/broadcast-channel.enum';
import { IVideo } from '@shared/interface/model.interface';
import { PlayerExtended } from '@shared/interface/videojs.interface';
import { dbGetActiveUser } from '@data/datasource/local/user.database';
import { VideojsTechEnum } from '@shared/enum/videojs.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { tailleFichier } from '@shared/lib/tools.lib';
import { logger } from '@shared/lib/logger/logger-manager';

interface TechExtended extends Tech {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSource?: (source: any) => void;
}

/**
 * HTML References
 */
const videoLoadError =
    'Impossible de lire la vidéo. Vous devez être connecté à internet ou avoir lu au moins une fois cette vidéo.';


/**
 * Reactive data
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'QVideoCustom',
  props: {
  video: { type: Object as PropType<IVideo | undefined>, required: true },
  programLabel: { type: String, required: true },
},
  emits: ['loading', 'loaded', 'close'],
  setup(__props, { emit: __emit }) {

// Vendor
const videoPlayer = ref<HTMLVideoElement>();

/**
 * Stores
 */
const uiStore = useUiStore();

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * Static data
 */
let videoChannel: BroadcastChannel | null = null;
const videoUrl = ref<string | undefined>(undefined);
const castVideoUrl = ref<string | undefined>(undefined);
const displayVideoTag = ref<boolean>(true);
const isPlaying = ref<boolean>(false);
const customWidth = ref<number>(0);
const error = ref<string | undefined>(undefined);
const player = ref<Player>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const videoPlayerOptions = ref<any>();
const videoPlayerController = ref<PlayerExtended>();
const chromecastConnected = ref<boolean>(false);

/**
 * Computed
 */
const isOnline = computed(() => sessionManager.isOnline.value === true);
const stringSecondaryUsers = computed<string | undefined>(() => {
  if (
    video.value?.secondaryUsers != null &&
    video.value?.secondaryUsers.length > 0
  ) {
    if (video.value.secondaryUsers.length == 1) {
      return video.value.secondaryUsers[0].username;
    } else {
      let temp: string[] = [];
      for (let i = 0; i < video.value.secondaryUsers.length - 1; i++) {
        temp.push(video.value.secondaryUsers[i].username ?? 'inconnu');
      }

      return temp
        .join(', ')
        .concat(' et ')
        .concat(
          video.value.secondaryUsers[video.value.secondaryUsers.length - 1]
            .username ?? 'inconnu',
        );
    }
  } else {
    return undefined;
  }
});

/**
 * Emits
 */
const emits = __emit;

/**
 * Properties
 */
const props = __props;
const { video, programLabel } = toRefs(props);

/**
 * Methods
 */
async function setCastUrl() {
  const baseURL = `${process.env.BACKEND.host ?? ''}${
    process.env.BACKEND.port ? ':' + process.env.BACKEND.port : ''
  }${process.env.BACKEND.context ?? ''}`;

  const activeUser = await dbGetActiveUser();

  return (
    baseURL +
    endpoints.video.getVideoForChromecast
      .addParams([video.value?.id ?? 0, activeUser?.refreshToken?.token ?? ''])
      .getUrl()
  );
}

function setUrl() {
  const baseURL = `${process.env.BACKEND.host ?? ''}${
    process.env.BACKEND.port ? ':' + process.env.BACKEND.port : ''
  }${process.env.BACKEND.context ?? ''}`;

  return (
    baseURL +
    endpoints.video.getStreamedVideo.addParams([video.value?.id ?? 0]).getUrl()
  );
}

/**
 * Methods
 */
function stopVideo() {
  if (isPlaying.value === true) {
    videoPlayer.value?.pause();
    isPlaying.value = false;
  }
  error.value = undefined;
  videoUrl.value = undefined;

  emits('loaded');
}

function playVideo() {
  videoPlayer.value?.play().then(() => {
    isPlaying.value = true;
    emits('loaded');

    if (video.value?.id != null) {
      uiStore.addVideo(video.value?.id);
    }
  });
}

function removeListeners() {
  videoPlayer.value?.removeEventListener('canplay', canplayListenerFunction);
}

function setVideoWidth() {
  const newWidth = videoPlayer.value?.videoWidth;
  const newHeight = videoPlayer.value?.videoHeight;
  const isLandscape =
    newWidth != null && newHeight != null && newHeight < newWidth;

  if (videoPlayer.value != null) {
    if (isLandscape) {
      customWidth.value = isMobile.value ? window.innerWidth * 0.9 : 550;
    } else {
      customWidth.value = isMobile.value ? window.innerWidth * 0.9 : 400;
    }
  }
}

/**
 * Video listeners
 */
function canplayListenerFunction() {
  displayVideoTag.value = true;
  error.value = undefined;
  setVideoWidth();
  playVideo();
}

function initVideoJsPlayer() {
  if (videoPlayer.value != null && castVideoUrl.value != null) {
    videojs.addLanguage('fr', frJson);

    videoPlayerOptions.value = {
      techOrder: [VideojsTechEnum.CHROMECAST, VideojsTechEnum.HTML5],
      liveui: true,
      html5: {
        hls: {
          enableLowInitialPlaylist: true,
          smoothQualityChange: true,
          overrideNative: true,
          useDevicePixelRatio: true
        },
        vhs: {
          overrideNative: true,
          useDevicePixelRatio: true,
        },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
      sources: [
        {
          src: videoUrl.value,
          type: 'video/mp4',
        },
      ],

      playbackRates: [0.25, 0.5, 1, 1.5, 2],
      inactivityTimeout: 5000,
      autoplay: true,
      controls: true,
      muted: true,
      fluid: true,
      // aspectRatio: '16:9',
      responsive: false,
      controlBar: {
        volumePanel: false,
        pictureInPictureToggle: false,
        chromecastButton: true,
      },
    };

    // Important to setup BEFORE videojs setup
    chromecast(videojs, { preloadWebComponents: true });
    const chromecastTech: TechExtended | undefined = videojs.getTech(
      VideojsTechEnum.CHROMECAST,
    );

    if (chromecastTech?.setSource != null) {
      chromecastTech.setSource({
        src: castVideoUrl.value,
        type: 'video/mp4',
      });
    }
    videoPlayerController.value = videojs(
      videoPlayer.value,
      videoPlayerOptions.value,
      function () {
        videoPlayerController.value?.play();
      },
    );

    if (videoPlayerController.value.chromecast != null) {
      videoPlayerController.value.chromecast();
    }
  }
}

/**
 * Watch properties
 */
watch(() => isOnline.value, (newVal: boolean) => {
  if (newVal != null) {
    if (newVal) {
      error.value = undefined;
      emits('loading');
    } else {
      if (video.value?.id != null && !uiStore.hasVideo(video.value.id)) {
        error.value = videoLoadError;
      } else {
        emits('loading');
        error.value = undefined;
      }
    }
  }
}, { immediate: true})

/**
 * Hooks
 */
onMounted(async () => {
  // Set initial values
  isPlaying.value = false;
  videoUrl.value = undefined;

  // Service Worker message
  if ('serviceWorker' in navigator) {
    videoChannel = new BroadcastChannel(BroadcastChannelEnum.SW_CHANNEL_VIDEO);
    videoChannel.onmessage = (event) => {
      if (
        event.data.message === BroadcastChannelMessageEnum.VIDEO_LOAD_FAILED
      ) {
        error.value = videoLoadError;
        emits('loaded');
      }
    };
  }

  // Check access token validity and renew it if needed
  await sessionManager.checkAccessTokenValidity()

  // Events
  videoPlayer.value?.addEventListener('canplay', canplayListenerFunction);

  // Set video URL and load it to the <video> tag
  videoUrl.value = setUrl();
  castVideoUrl.value = await setCastUrl();

  initVideoJsPlayer();

  videoPlayerController.value?.on('chromecastConnected', () => {
    chromecastConnected.value = true;
  });

  videoPlayerController.value?.on('chromecastDisconnected', () => {
    emits('close');
  });

  videoPlayerController.value?.on('chromecastRequested', () => {
    if (castVideoUrl.value != null && chromecastConnected.value == false) {
      videoPlayerController.value
        ?.tech()
        .setAttribute('src', castVideoUrl.value);
    }
  });
});

onBeforeUnmount(() => {
  removeListeners();

  stopVideo();

  if (player.value) {
    player.value.dispose();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_Transition, {
      appear: "",
      "enter-active-class": "animated fadeIn",
      "leave-active-class": "animated fadeOut"
    }, {
      default: _withCtx(() => [
        (displayVideoTag.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("video", {
                lang: "fr",
                class: "video-js",
                ref_key: "videoPlayer",
                ref: videoPlayer,
                width: customWidth.value,
                "aria-labelledby": "video_legend",
                crossorigin: "use-credentials"
              }, _hoisted_5, 8 /* PROPS */, _hoisted_3),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("span", _hoisted_7, "\"" + _toDisplayString(_unref(programLabel).toLocaleUpperCase()) + "\"", 1 /* TEXT */),
                _createTextVNode(" exécuté par " + _toDisplayString(_unref(video)?.mainUser?.username) + _toDisplayString(stringSecondaryUsers.value != null
              ? ` et accompagné de ${stringSecondaryUsers.value}`
              : '') + _toDisplayString(_unref(video)?.fileSize != null
              ? ' (taille de la vidéo : ' + _unref(tailleFichier)(_unref(video)?.fileSize) + ')'
              : ''), 1 /* TEXT */)
              ])
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }),
    _createVNode(_Transition, {
      appear: "",
      "enter-active-class": "animated fadeIn",
      "leave-active-class": "animated fadeOut",
      persisted: ""
    }, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", null, _toDisplayString(error.value), 513 /* TEXT, NEED_PATCH */), [
          [_vShow, error.value != null]
        ])
      ]),
      _: 1 /* STABLE */
    })
  ]))
}
}

})